import { Button, Card, Modal } from "antd";
import { useEffect, useRef, useState } from "react";

interface Prop {
  open: boolean;
  onCancel: () => void;
  onSaveClick: (rectangles: any) => void;
  imageUrl: any;
  initialCoords?: any[];
}

const CanvasModal = ({
  open,
  onCancel,
  imageUrl,
  onSaveClick,
  initialCoords,
}: Prop) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [currentRect, setCurrentRect] = useState({
    startX: 0,
    startY: 0,
    width: 0,
    height: 0,
  });
  const [rectangles, setRectangles] = useState<any>(initialCoords || []);
  const [image, setImage] = useState<HTMLImageElement | null>(null);
  const [loading, setLoading] = useState(true);
  const [hoveredDeleteButtonIndex, setHoveredDeleteButtonIndex] = useState<
    number | null
  >(null);
  const [cursorStyle, setCursorStyle] = useState("crosshair");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isPreviewOverlapping, setIsPreviewOverlapping] = useState(false);

  useEffect(() => {
    if (imageUrl) {
      const img = new Image();
      img.src = imageUrl;
      img.onload = () => {
        setImage(img);
        setLoading(false);
      };
    }
  }, [imageUrl]);

  useEffect(() => {
    if (image && canvasRef.current) {
      redrawCanvas();
    }
  }, [image, rectangles, hoveredDeleteButtonIndex]);

  useEffect(() => {
    if (image && initialCoords) {
      setRectangles(initialCoords);
    }
  }, [image, initialCoords]);

  const redrawCanvas = () => {
    const canvas = canvasRef.current;

    if (canvas && image) {
      const context: any = canvas.getContext("2d");
      context?.clearRect(0, 0, canvas.width, canvas.height);
      context?.drawImage(image, 0, 0, canvas.width, canvas.height);

      rectangles.forEach((rect: any, index: number) => {
        // Add red background with opacity
        context.fillStyle = "rgba(255, 0, 0, 0.2)";
        context?.fillRect(
          rect.startX * canvas.width,
          rect.startY * canvas.height,
          rect.width * canvas.width,
          rect.height * canvas.height
        );

        // Draw the red border
        context.strokeStyle = "red";
        context.lineWidth = 1;
        context?.strokeRect(
          rect.startX * canvas.width,
          rect.startY * canvas.height,
          rect.width * canvas.width,
          rect.height * canvas.height
        );

        // Draw delete button in top-right corner of each rectangle
        const buttonSize = 16;
        const padding = 2;
        const buttonX =
          (rect.startX + rect.width) * canvas.width - buttonSize - padding;
        const buttonY = rect.startY * canvas.height + padding;

        // Draw delete button (X)
        context.fillStyle =
          hoveredDeleteButtonIndex === index ? "#ff4d4f" : "#f5222d";
        context.beginPath();
        context.arc(
          buttonX + buttonSize / 2,
          buttonY + buttonSize / 2,
          buttonSize / 2,
          0,
          Math.PI * 2
        );
        context.fill();

        // Draw X in the button
        context.strokeStyle = "white";
        context.lineWidth = 2;
        context.beginPath();
        context.moveTo(buttonX + 4, buttonY + 4);
        context.lineTo(buttonX + buttonSize - 4, buttonY + buttonSize - 4);
        context.moveTo(buttonX + buttonSize - 4, buttonY + 4);
        context.lineTo(buttonX + 4, buttonY + buttonSize - 4);
        context.stroke();
      });
    }
  };

  const handleMouseDown = (e: any) => {
    const canvas = canvasRef.current;
    const rect = canvas?.getBoundingClientRect();

    if (rect && canvas) {
      const mouseX = (e.clientX - rect.left) / rect.width;
      const mouseY = (e.clientY - rect.top) / rect.height;

      // Check if any delete button was clicked
      const buttonSize = 16 / rect.width; // Convert to relative coordinates
      const padding = 2 / rect.width; // Convert to relative coordinates

      for (let i = rectangles.length - 1; i >= 0; i--) {
        const rectangle = rectangles[i];
        const buttonX =
          rectangle.startX + rectangle.width - buttonSize - padding;
        const buttonY = rectangle.startY + padding;

        // Check if click was inside delete button
        if (
          mouseX >= buttonX &&
          mouseX <= buttonX + buttonSize &&
          mouseY >= buttonY &&
          mouseY <= buttonY + buttonSize
        ) {
          handleDelete(i);
          return;
        }
      }

      // If no delete button was clicked, start drawing a new rectangle
      setCurrentRect({
        startX: mouseX,
        startY: mouseY,
        width: 0,
        height: 0,
      });
      setIsDrawing(true);
      setErrorMessage(null);
    }
  };

  const handleMouseMove = (e: any) => {
    const canvas = canvasRef.current;
    const rect = canvas?.getBoundingClientRect();

    if (rect && canvas) {
      const mouseX = (e.clientX - rect.left) / rect.width;
      const mouseY = (e.clientY - rect.top) / rect.height;

      // Check if mouse is over any delete button
      const buttonSize = 16 / rect.width; // Convert to relative coordinates
      const padding = 2 / rect.width; // Convert to relative coordinates
      let hoveredButton = null;
      let isOverButton = false;

      for (let i = rectangles.length - 1; i >= 0; i--) {
        const rectangle = rectangles[i];
        const buttonX =
          rectangle.startX + rectangle.width - buttonSize - padding;
        const buttonY = rectangle.startY + padding;

        // Check if mouse is over delete button
        if (
          mouseX >= buttonX &&
          mouseX <= buttonX + buttonSize &&
          mouseY >= buttonY &&
          mouseY <= buttonY + buttonSize
        ) {
          hoveredButton = i;
          isOverButton = true;
          break;
        }
      }

      // Update cursor style based on whether mouse is over a delete button
      if (isOverButton) {
        setCursorStyle("pointer");
      } else {
        setCursorStyle("crosshair");
      }

      // Update hovered button state
      if (hoveredButton !== hoveredDeleteButtonIndex) {
        setHoveredDeleteButtonIndex(hoveredButton);
      }

      // If drawing, update the current rectangle
      if (isDrawing) {
        const newRect = {
          startX: currentRect.startX,
          startY: currentRect.startY,
          width: mouseX - currentRect.startX,
          height: mouseY - currentRect.startY,
        };

        // Normalize for overlap check
        const normalizedRect = {
          startX:
            newRect.width < 0 ? newRect.startX + newRect.width : newRect.startX,
          startY:
            newRect.height < 0
              ? newRect.startY + newRect.height
              : newRect.startY,
          width: Math.abs(newRect.width),
          height: Math.abs(newRect.height),
        };

        // Check for overlap
        const hasOverlap = rectangles.some((rect: any) =>
          isOverlapping(rect, normalizedRect)
        );
        setIsPreviewOverlapping(hasOverlap);

        if (hasOverlap) {
          setErrorMessage("You can't overlap");
        } else {
          setErrorMessage(null);
          setCurrentRect(newRect);
        }

        // Redraw canvas with the current rectangle only if not overlapping
        const context: any = canvas.getContext("2d");
        redrawCanvas();

        if (!hasOverlap) {
          // Add semi-transparent red fill for current rectangle being drawn
          context.fillStyle = "rgba(255, 0, 0, 0.2)";
          context?.fillRect(
            newRect.startX * canvas.width,
            newRect.startY * canvas.height,
            newRect.width * canvas.width,
            newRect.height * canvas.height
          );

          // Add red border
          context.strokeStyle = "red";
          context.lineWidth = 1;
          context?.strokeRect(
            newRect.startX * canvas.width,
            newRect.startY * canvas.height,
            newRect.width * canvas.width,
            newRect.height * canvas.height
          );
        }
      }
    }
  };

  const handleMouseUp = () => {
    if (
      isDrawing &&
      (Math.abs(currentRect.width) > 0.01 ||
        Math.abs(currentRect.height) > 0.01) &&
      !isPreviewOverlapping
    ) {
      // Ensure rectangles have positive width and height
      const normalizedRect = {
        startX:
          currentRect.width < 0
            ? currentRect.startX + currentRect.width
            : currentRect.startX,
        startY:
          currentRect.height < 0
            ? currentRect.startY + currentRect.height
            : currentRect.startY,
        width: Math.abs(currentRect.width),
        height: Math.abs(currentRect.height),
      };

      setRectangles((prevRectangles: any) => [
        ...prevRectangles,
        normalizedRect,
      ]);
      setErrorMessage(null);
    }
    setIsDrawing(false);
    setIsPreviewOverlapping(false);
  };

  const handleDelete = (index: number) => {
    setRectangles((prevRectangles: any) => {
      const newRectangles = [...prevRectangles];
      newRectangles.splice(index, 1);
      return newRectangles;
    });
  };

  // Check if two rectangles overlap
  const isOverlapping = (rectA: any, rectB: any) => {
    return !(
      rectA.startX + rectA.width <= rectB.startX ||
      rectB.startX + rectB.width <= rectA.startX ||
      rectA.startY + rectA.height <= rectB.startY ||
      rectB.startY + rectB.height <= rectA.startY
    );
  };

  return (
    <Modal
      title={"Mark Pages"}
      open={open}
      centered
      onCancel={() => onCancel()}
      footer={
        <div>
          <Button
            danger
            size="large"
            style={{ marginRight: "10px" }}
            onClick={() => onCancel()}
          >
            Cancel
          </Button>
          <Button
            size="large"
            type="primary"
            onClick={() => onSaveClick(rectangles)}
          >
            Save
          </Button>
        </div>
      }
      width={"80%"}
    >
      <br />
      <div style={{ display: "flex", flexDirection: "row", gap: 10 }}>
        <div style={{ width: "100%" }}>
          {loading ? (
            "Loading..."
          ) : (
            <>
              {errorMessage && (
                <div
                  style={{
                    color: "red",
                    marginBottom: 8,
                    fontWeight: "bold",
                    textAlign: "center",
                  }}
                >
                  {errorMessage}
                </div>
              )}
              <Card title={"Draw Boxes"}>
                <canvas
                  ref={canvasRef}
                  width={800}
                  height={1000}
                  style={{ width: "100%", height: "auto", cursor: cursorStyle }}
                  onMouseDown={handleMouseDown}
                  onMouseMove={handleMouseMove}
                  onMouseUp={handleMouseUp}
                  onMouseLeave={() => {
                    if (isDrawing) setIsDrawing(false);
                    setHoveredDeleteButtonIndex(null);
                    setCursorStyle("crosshair");
                  }}
                />
              </Card>
            </>
          )}
        </div>
      </div>
      <hr />
    </Modal>
  );
};

export default CanvasModal;
